/**
 * @fileoverview カードコンポーネントをランキング表示する用のレイアウトコンポーネント
 */
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import type { IntRange } from 'type-fest'
import ContentsCard from '@/components/contents/ContentsCard.vue'
import StoryCard from '@/components/contents/StoryCard.vue'
import SolutionContentsCard from '@/components/solutions/SolutionContentsCard.vue'

/**
 * index: ランキングページ - 急上昇タブ
 * hallOfFame: ランキングページ - 殿堂入りタブ
 * stories: ランキングページ - 特集タブ
 * keywords: ランキングページ - 用語タブ
 * comments: ランキングページ - コメントタブ
 * documents: ランキングページ - 資料タブ
 * aside: その他 - 右袖のアサイドカラムの人気記事ランキングエリア（ホーム、特集詳細、記事詳細、マイページなど）
 */
const RANKING_CATEGORIES = ['index', 'hallOfFame', 'stories', 'keywords', 'comments', 'documents', 'aside'] as const
type RankingCategory = typeof RANKING_CATEGORIES[number]

export default defineComponent({
  name: 'LayoutRankingContentsCards',

  components: {
    ContentsCard,
    StoryCard,
    SolutionContentsCard,
  },

  props: {
    /**
     * 資料情報の配列
     */
    items: {
      type: Array as PropType<ContentItem[]>,
      required: true,
    },

    category: {
      type: String as PropType<RankingCategory>,
      default: 'index',
      validator: (value: string) => {
        return (RANKING_CATEGORIES as ReadonlyArray<string>).includes(value)
      },
    },
  },

  setup() {
    const layoutOptions = (category: RankingCategory, rank: IntRange<1, 11> | undefined) => {
      let hasHero = true
      const _imageOptions = {
        width: {
          pc: 0,
          sp: 0,
        },
        height: {
          pc: 0,
          sp: 0,
        },
      }

      const _horizontal = {
        pc: false,
        sp: false,
      }

      const _headingOptions = {
        fontSize: {
          pc: '',
          sp: '',
        },
        maxLine: 0
      }

      if (rank) {
        switch(category) {
          case 'index':
            /**
             * 1 〜 ３ 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // PC 画像サイズ（SP 時は縦型になるので指定なし）
              _imageOptions.width.pc = 190
              _imageOptions.height.pc = 100

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 72
              _imageOptions.height.pc = 72

              // SP 画像サイズ
              _imageOptions.width.sp = 72
              _imageOptions.height.sp = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = true
            }

            // 最大行数
            _headingOptions.maxLine = 2
            break

          case 'hallOfFame':
            /**
             * 1 位
             */
            if (rank === 1) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // 最大行数
              _headingOptions.maxLine = 3

              // PC 画像サイズ（SP 時は縦型になるので指定なし）
              _imageOptions.width.pc = 197
              _imageOptions.height.pc = 103

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 2 〜 3 位
             */
            } else if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // 最大行数
              _headingOptions.maxLine = 3

              // PC 画像サイズ（SP 時は縦型になるので指定なし）
              _imageOptions.width.pc = 72
              _imageOptions.height.pc = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // 最大行数
              _headingOptions.maxLine = 2

              // PC 画像サイズ
              _imageOptions.width.pc = 152
              _imageOptions.height.pc = 80

              // SP 画像サイズ
              _imageOptions.width.sp = 72
              _imageOptions.height.sp = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = true
            }
            break

          case 'stories':
            /**
             * 1 〜 3 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 200
              _imageOptions.height.pc = 105

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 136
              _imageOptions.height.pc = 72

              // SP 画像サイズ
              _imageOptions.width.sp = 136
              _imageOptions.height.sp = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = true
            }

            // 最大行数
            _headingOptions.maxLine = 2
            break

          case 'keywords':
            hasHero = false

            /**
             * 1 〜 ３ 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'
            }

            // 最大行数
            _headingOptions.maxLine = 2

            break

          case 'comments':
            /**
             * 1 〜 ３ 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // PC 画像サイズ（SP 時は縦型になるので指定なし）
              _imageOptions.width.pc = 100
              _imageOptions.height.pc = 100

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 72
              _imageOptions.height.pc = 72

              // SP 画像サイズ
              _imageOptions.width.sp = 72
              _imageOptions.height.sp = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = true
            }

            // 最大行数
            _headingOptions.maxLine = 2

            break

          case 'documents':
            /**
             * 1 〜 ３ 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '2.2rem'
              _headingOptions.fontSize.sp = '1.8rem'

              // PC 画像サイズ（SP 時は縦型になるので指定なし）
              _imageOptions.width.pc = 200
              _imageOptions.height.pc = 100

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = false

            /**
             * 4 位以降
             */
            } else {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.8rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 136
              _imageOptions.height.pc = 72

              // SP 画像サイズ
              _imageOptions.width.sp = 136
              _imageOptions.height.sp = 72

              // レイアウト方向指定
              _horizontal.pc = true
              _horizontal.sp = true
            }

            // 最大行数
            _headingOptions.maxLine = 2

            break

          default:
            /**
             * 1 〜 ３ 位
             */
            if (rank <= 3) {
              // フォントサイズ
              _headingOptions.fontSize.pc = '1.6rem'
              _headingOptions.fontSize.sp = '1.6rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 72
              _imageOptions.height.pc = 72

              // SP 画像サイズ
              _imageOptions.width.sp = 72
              _imageOptions.height.sp = 72


            /**
             * 4 位以降
             */
            } else {
              hasHero = false

              // フォントサイズ
              _headingOptions.fontSize.pc = '1.4rem'
              _headingOptions.fontSize.sp = '1.4rem'

              // PC 画像サイズ
              _imageOptions.width.pc = 0
              _imageOptions.height.pc = 0

              // SP 画像サイズ
              _imageOptions.width.sp = 0
              _imageOptions.height.sp = 0

            }

            // レイアウト方向指定
            _horizontal.pc = true
            _horizontal.sp = true

            // 最大行数
            _headingOptions.maxLine = 2

            break

        }
      }

      return {
        hasHero: hasHero,
        headingOptions: _headingOptions,
        imageOptions: _imageOptions,
        horiozntal: _horizontal,
      }
    }

    const validRank = (rank: number) => {
      return rank >= 1 && rank <= 10
        ? rank as IntRange<1, 11>
        : undefined
    }

    return {
      // methods
      layoutOptions,
      validRank,
    }
  }
})
</script>

<template>
  <div
    v-if="items.length"
    :class="[
      'o-list__ranking',
      `is-${category}`,
    ]">
    <ol class="o-list__ranking--items">
      <li
        v-for="(item, index) in items"
        :key="item.id"
        class="o-list__ranking--item"
        :data-rank="index + 1">
        <!--
          item に solution（製品）情報を持ってる場合は資料データのため、
          製品用のコンテンツカードをレンダリング
        -->
        <template v-if="item.solution">
          <SolutionContentsCard
            :item="item"
            :has-solution-link="false"
            :heading-options="{
              maxLine: layoutOptions(category, validRank(index + 1)).headingOptions.maxLine,
              fontSize: layoutOptions(category, validRank(index + 1)).headingOptions.fontSize,
            }"
            :image-options="layoutOptions(category, validRank(index + 1)).imageOptions"
            :horizontal="layoutOptions(category, validRank(index + 1)).horiozntal" />
        </template>
        <template v-else>
          <!-- 特集 -->
          <template v-if="category === 'stories'">
            <StoryCard
              :item="item"
              :has-hero="layoutOptions(category, validRank(index + 1)).hasHero"
              :heading-options="{
                maxLine: layoutOptions(category, validRank(index + 1)).headingOptions.maxLine,
                fontSize: layoutOptions(category, validRank(index + 1)).headingOptions.fontSize,
              }"
              :image-options="layoutOptions(category, validRank(index + 1)).imageOptions"
              :horizontal="layoutOptions(category, validRank(index + 1)).horiozntal"
              has-theme />
          </template>

          <!-- 特集以外のコンテンツ -->
          <template v-else>
            <ContentsCard
              :item="item"
              :has-hero="layoutOptions(category, validRank(index + 1)).hasHero"
              :heading-options="{
                maxLine: layoutOptions(category, validRank(index + 1)).headingOptions.maxLine,
                fontSize: layoutOptions(category, validRank(index + 1)).headingOptions.fontSize,
              }"
              :image-options="layoutOptions(category, validRank(index + 1)).imageOptions"
              :horizontal="layoutOptions(category, validRank(index + 1)).horiozntal" />
          </template>
        </template>
      </li>
    </ol>
  <!-- /.o-list__ranking --></div>
</template>

<style lang="sass" scoped>
.o-list__ranking
  $root: &
  $gap-size: 20
  $top-tier-icon-size: 40
  $low-tier-icon-size: 24
  $icon-margin: 16
  margin: changeSizePx2Em(24) auto 0

  &:first-child
    margin-top: 0

  & &--items
    display: flex
    flex-direction: column

  & &--item
    position: relative
    margin-top: #{$gap-size}px
    padding-left: #{$top-tier-icon-size + $icon-margin}px

    &::before
      content: attr(data-rank)
      width: #{$low-tier-icon-size}px
      height: #{$low-tier-icon-size}px
      display: block
      position: absolute
      top: 0
      left: #{math.div(($top-tier-icon-size - $low-tier-icon-size), 2)}px
      overflow: hidden
      line-height: #{$low-tier-icon-size}px
      color: $font-orange
      font:
        size: $font-small
        family: Montserrat, 'Montserrat', sans-serif
      text-align: center
      background-color: rgba($bg-white, 1)
      border-radius: 50%

    &[data-rank="1"],
    &[data-rank="2"],
    &[data-rank="3"]
      &::before
        content: ""
        width: #{$top-tier-icon-size}px
        height: #{$top-tier-icon-size}px
        left: 0
        background:
          color: transparent
          repeat: no-repeat
          position: center center
          size: contain
        border-radius: 0

    &[data-rank="1"]
      &::before
        background-image: url(@/images/icon/ranking/icon-rank-1_01.svg)

    &[data-rank="2"]
      &::before
        background-image: url(@/images/icon/ranking/icon-rank-2_01.svg)

    &[data-rank="3"]
      &::before
        background-image: url(@/images/icon/ranking/icon-rank-3_01.svg)

    &:first-child
      margin-top: 0

  &.is-hallOfFame
    $top-tier-icon-width: 72
    $top-tier-icon-height: 49
    $icon-margin: 8
    $gap-size: 16
    #{$root}--items
      flex-direction: row
      flex-wrap: wrap

    #{$root}--item
      width: 100%

      &[data-rank="1"],
      &[data-rank="2"],
      &[data-rank="3"]
        padding: #{$gap-size}px
        padding-left: #{$top-tier-icon-width + $icon-margin * 2}px

        &::before
          width: #{$top-tier-icon-width}px
          height: #{$top-tier-icon-height}px
          top: #{$gap-size}px
          left: #{$icon-margin}px

      &[data-rank="2"],
      &[data-rank="3"]
        width: calc((100% - 1em) / 2)
        margin-top: #{$gap-size}px

      &[data-rank="1"]
        border: 1px solid $border-rank-1

        &::before
          background-image: url(@/images/icon/ranking/icon-rank-1_02.svg)

      &[data-rank="2"]
        border: 1px solid $border-rank-2

        &::before
          background-image: url(@/images/icon/ranking/icon-rank-2_02.svg)

      &[data-rank="3"]
        margin-left: #{$gap-size}px
        border: 1px solid $border-rank-3

        &::before
          background-image: url(@/images/icon/ranking/icon-rank-3_02.svg)

      &[data-rank="4"]
        margin-top: #{$gap-size * 2}px

  &.is-aside
    $top-tier-icon-size: 24
    $low-tier-icon-size: 24
    $icon-margin: 8
    margin: changeSizePx2Em(24) auto 0

    &:first-child
      margin-top: 0

    #{$root}--item
      padding-left: #{$top-tier-icon-size + $icon-margin}px

      &::before
        width: #{$low-tier-icon-size}px
        height: #{$low-tier-icon-size}px
        left: 0
        line-height: #{$low-tier-icon-size}px

      &[data-rank="1"],
      &[data-rank="2"],
      &[data-rank="3"]
        &::before
          content: attr(data-rank)
          width: #{$top-tier-icon-size}px
          height: #{$top-tier-icon-size}px
          left: 0
          color: $font-white
          background-color: rgba($bg-dark-orange, 1)
          border-radius: 50%

      &[data-rank="1"]
        &::before
          background-image: none

      &[data-rank="2"]
        &::before
          background-image: none

      &[data-rank="3"]
        &::before
          background-image: none

      &:first-child
        margin-top: 0

  +breakpoint
    $gap-size: 12
    #{$root}--item
      margin-top: #{$gap-size}px
      padding-top: #{$gap-size}px
      border-top: 1px solid $border-x-light-gray

      &::before
        top: #{$gap-size}px
        left: 0

      &:first-child
        padding-top: 0
        border-top: none

        &::before
          top: 0

      &:not([data-rank="1"]):not([data-rank="2"]):not([data-rank="3"])
        $icon-margin: 8
        padding-left: #{$low-tier-icon-size + $icon-margin}px

        &:first-child
          margin-top: 0
          padding-top: 0
          border-top: none

          &::before
            top: 0

    &.is-hallOfFame
      #{$root}--items
        flex-direction: column
        flex-wrap: nowrap

      #{$root}--item
        &[data-rank="1"],
        &[data-rank="2"],
        &[data-rank="3"]
          padding: #{$gap-size}px

          &::before
            position: static
            margin:
              bottom: #{$gap-size}px
              left: auto
              right: auto

        &[data-rank="2"],
        &[data-rank="3"]
          width: 100%
          margin-top: #{$gap-size}px

        &[data-rank="3"]
          margin-left: 0

        &[data-rank="4"]
          margin-top: #{$gap-size * 2}px
          padding-top: 0
          border-top: none
</style>
